/**
 * Charts, drawn by hand in SVG.
 *
 * Three shapes cover every screen in this product: a line over time, bars with
 * a line over them, and a donut. Recharts alone is ~180KB gzipped for that, on
 * a dashboard whose median session is a merchant checking today's orders on a
 * phone. These render server-identically, scale with the container, and read
 * their colours from the same CSS tokens as everything else.
 *
 * All three take a `formatValue` rather than formatting internally — money in
 * this app is a decimal string and must not round-trip through Number() on the
 * way to the screen.
 */

import { useId, useMemo, useState, type ReactNode } from 'react'

export interface Series {
  key: string
  label: string
  color: string
  values: number[]
  /** Renders as a dashed line — used for the "previous period" comparison. */
  dashed?: boolean
}

interface ChartFrame {
  width: number
  height: number
  padTop: number
  padBottom: number
  padLeft: number
}

const FRAME: ChartFrame = { width: 720, height: 240, padTop: 12, padBottom: 26, padLeft: 46 }

/** Nice round ceiling, so the top gridline is 2L rather than 1.87L. */
function niceMax(value: number): number {
  if (value <= 0) return 1
  const magnitude = 10 ** Math.floor(Math.log10(value))
  const normalised = value / magnitude
  const step = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 2.5 ? 2.5 : normalised <= 5 ? 5 : 10
  return step * magnitude
}

/** Short Indian-numbering axis labels: 50K, 1.5L, 2Cr. */
export function compact(value: number): string {
  if (Math.abs(value) >= 1e7) return `${trim(value / 1e7)}Cr`
  if (Math.abs(value) >= 1e5) return `${trim(value / 1e5)}L`
  if (Math.abs(value) >= 1e3) return `${trim(value / 1e3)}K`
  return String(Math.round(value))
}

function trim(value: number): string {
  return value.toFixed(1).replace(/\.0$/, '')
}

function xAxisLabels(labels: string[], max = 8): Array<{ label: string; index: number }> {
  if (labels.length <= max) return labels.map((label, index) => ({ label, index }))
  const stride = Math.ceil(labels.length / max)
  return labels.reduce<Array<{ label: string; index: number }>>((out, label, index) => {
    if (index % stride === 0) out.push({ label, index })
    return out
  }, [])
}

function Legend({ series }: { series: Array<{ label: string; color: string }> }): ReactNode {
  return (
    <div className="chart__legend">
      {series.map((entry) => (
        <span key={entry.label}>
          <span className="chart__swatch" style={{ background: entry.color }} />
          {entry.label}
        </span>
      ))}
    </div>
  )
}

// ── Line chart ──────────────────────────────────────────────────────────────

export function LineChart({
  labels,
  series,
  formatValue = compact,
  height = 240,
  area = true,
}: {
  labels: string[]
  series: Series[]
  formatValue?: (value: number) => string
  height?: number
  area?: boolean
}): ReactNode {
  const gradientId = useId()
  const [hover, setHover] = useState<number | null>(null)

  const max = useMemo(() => niceMax(Math.max(1, ...series.flatMap((s) => s.values))), [series])
  const { width, padTop, padBottom, padLeft } = FRAME
  const plotH = height - padTop - padBottom
  const plotW = width - padLeft
  const step = labels.length > 1 ? plotW / (labels.length - 1) : plotW

  const x = (index: number): number => padLeft + index * step
  const y = (value: number): number => padTop + plotH - (value / max) * plotH

  const gridlines = [0, 0.25, 0.5, 0.75, 1]

  return (
    <div className="chart">
      <Legend series={series} />

      <svg viewBox={`0 0 ${width} ${height}`} height={height} role="img" aria-label={series.map((s) => s.label).join(' and ')} preserveAspectRatio="none">
        <defs>
          {series.map((entry) => (
            <linearGradient key={entry.key} id={`${gradientId}-${entry.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={entry.color} stopOpacity="0.18" />
              <stop offset="100%" stopColor={entry.color} stopOpacity="0" />
            </linearGradient>
          ))}
        </defs>

        {gridlines.map((fraction) => {
          const lineY = padTop + plotH * (1 - fraction)
          return (
            <g key={fraction}>
              <line className="chart__grid" x1={padLeft} x2={width} y1={lineY} y2={lineY} />
              <text x={padLeft - 8} y={lineY + 4} textAnchor="end" fontSize="11" fill="var(--ink-faint)">
                {formatValue(max * fraction)}
              </text>
            </g>
          )
        })}

        {series.map((entry) => {
          const points = entry.values.map((value, index) => `${x(index)},${y(value)}`)
          if (points.length === 0) return null

          return (
            <g key={entry.key}>
              {area && !entry.dashed && (
                <path
                  d={`M ${x(0)},${padTop + plotH} L ${points.join(' L ')} L ${x(entry.values.length - 1)},${padTop + plotH} Z`}
                  fill={`url(#${gradientId}-${entry.key})`}
                />
              )}
              <path
                className="chart__line"
                d={`M ${points.join(' L ')}`}
                stroke={entry.color}
                strokeDasharray={entry.dashed ? '6 5' : undefined}
                opacity={entry.dashed ? 0.65 : 1}
              />
              {entry.values.map((value, index) => (
                <circle
                  key={index}
                  className="chart__dot"
                  cx={x(index)}
                  cy={y(value)}
                  r={hover === index ? 5 : 3.5}
                  fill={entry.color}
                  opacity={entry.dashed ? 0.65 : 1}
                />
              ))}
            </g>
          )
        })}

        {/* One transparent band per bucket, so the whole column is a hover
            target rather than the 3px dot itself. */}
        {labels.map((label, index) => (
          <rect
            key={label + String(index)}
            x={x(index) - step / 2}
            y={padTop}
            width={step}
            height={plotH}
            fill="transparent"
            onMouseEnter={() => setHover(index)}
            onMouseLeave={() => setHover(null)}
          >
            <title>
              {label}
              {series.map((s) => ` · ${s.label}: ${formatValue(s.values[index] ?? 0)}`).join('')}
            </title>
          </rect>
        ))}
      </svg>

      <div className="chart__axis-x" style={{ paddingLeft: `${(padLeft / width) * 100}%` }}>
        {xAxisLabels(labels).map((entry) => (
          <span key={`${entry.label}-${entry.index}`}>{entry.label}</span>
        ))}
      </div>
    </div>
  )
}

// ── Bars, optionally with a line over them ──────────────────────────────────

export function BarChart({
  labels,
  bars,
  line,
  formatValue = compact,
  height = 240,
}: {
  labels: string[]
  bars: Series
  line?: Series
  formatValue?: (value: number) => string
  height?: number
}): ReactNode {
  const barMax = useMemo(() => niceMax(Math.max(1, ...bars.values)), [bars])
  // The line gets its own scale. Visitors and orders differ by an order of
  // magnitude; sharing an axis flattens the orders line onto the floor.
  const lineMax = useMemo(() => (line ? niceMax(Math.max(1, ...line.values)) : 1), [line])

  const { width, padTop, padBottom, padLeft } = FRAME
  const plotH = height - padTop - padBottom
  const plotW = width - padLeft - 34
  const slot = plotW / Math.max(1, labels.length)
  const barW = Math.max(4, Math.min(30, slot * 0.55))

  const cx = (index: number): number => padLeft + slot * index + slot / 2
  const barY = (value: number): number => padTop + plotH - (value / barMax) * plotH
  const lineY = (value: number): number => padTop + plotH - (value / lineMax) * plotH

  const gridlines = [0, 0.25, 0.5, 0.75, 1]

  return (
    <div className="chart">
      <Legend series={line ? [bars, line] : [bars]} />

      <svg viewBox={`0 0 ${width} ${height}`} height={height} role="img" aria-label={bars.label} preserveAspectRatio="none">
        {gridlines.map((fraction) => {
          const gy = padTop + plotH * (1 - fraction)
          return (
            <g key={fraction}>
              <line className="chart__grid" x1={padLeft} x2={width - 34} y1={gy} y2={gy} />
              <text x={padLeft - 8} y={gy + 4} textAnchor="end" fontSize="11" fill="var(--ink-faint)">
                {formatValue(barMax * fraction)}
              </text>
              {line && (
                <text x={width - 26} y={gy + 4} fontSize="11" fill="var(--ink-faint)">
                  {compact(lineMax * fraction)}
                </text>
              )}
            </g>
          )
        })}

        {bars.values.map((value, index) => (
          <rect
            key={index}
            className="chart__bar"
            x={cx(index) - barW / 2}
            y={barY(value)}
            width={barW}
            height={Math.max(1, padTop + plotH - barY(value))}
            fill={bars.color}
            opacity="0.85"
          >
            <title>
              {labels[index]} · {bars.label}: {formatValue(value)}
            </title>
          </rect>
        ))}

        {line && (
          <>
            <path className="chart__line" d={`M ${line.values.map((v, i) => `${cx(i)},${lineY(v)}`).join(' L ')}`} stroke={line.color} />
            {line.values.map((value, index) => (
              <circle key={index} className="chart__dot" cx={cx(index)} cy={lineY(value)} r="3.5" fill={line.color}>
                <title>
                  {labels[index]} · {line.label}: {compact(value)}
                </title>
              </circle>
            ))}
          </>
        )}
      </svg>

      <div className="chart__axis-x" style={{ paddingLeft: `${(padLeft / width) * 100}%`, paddingRight: `${(34 / width) * 100}%` }}>
        {xAxisLabels(labels).map((entry) => (
          <span key={`${entry.label}-${entry.index}`}>{entry.label}</span>
        ))}
      </div>
    </div>
  )
}

// ── Donut ───────────────────────────────────────────────────────────────────

export interface Slice {
  key: string
  label: string
  value: number
  color: string
  /** Shown on the right of the legend — money stays a string all the way here. */
  display?: string
}

export function Donut({
  slices,
  centreValue,
  centreLabel,
  size = 172,
  thickness = 22,
}: {
  slices: Slice[]
  centreValue: ReactNode
  centreLabel?: string
  size?: number
  thickness?: number
}): ReactNode {
  const total = slices.reduce((sum, slice) => sum + slice.value, 0)
  const radius = (size - thickness) / 2
  const circumference = 2 * Math.PI * radius

  let consumed = 0

  return (
    <div className="donut">
      <div className="donut__figure" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${total} total`}>
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--surface-3)" strokeWidth={thickness} />
          {total > 0 &&
            slices.map((slice) => {
              const fraction = slice.value / total
              // A 1.5px gap between arcs, so two adjacent slices of similar
              // colour do not read as one.
              const length = Math.max(0, fraction * circumference - 1.5)
              const offset = -consumed * circumference
              consumed += fraction

              return (
                <circle
                  key={slice.key}
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={slice.color}
                  strokeWidth={thickness}
                  strokeDasharray={`${length} ${circumference - length}`}
                  strokeDashoffset={offset}
                  strokeLinecap="butt"
                  transform={`rotate(-90 ${size / 2} ${size / 2})`}
                >
                  <title>
                    {slice.label}: {slice.display ?? slice.value.toLocaleString('en-IN')} ({Math.round(fraction * 100)}%)
                  </title>
                </circle>
              )
            })}
        </svg>
        <div className="donut__centre">
          <strong>{centreValue}</strong>
          {centreLabel && <small>{centreLabel}</small>}
        </div>
      </div>

      <ul className="donut__legend">
        {slices.map((slice) => (
          <li key={slice.key}>
            <span className="chart__swatch" style={{ background: slice.color, borderRadius: '50%' }} />
            <span>{slice.label}</span>
            <strong>{slice.display ?? slice.value.toLocaleString('en-IN')}</strong>
            <small>{total > 0 ? `${((slice.value / total) * 100).toFixed(1)}%` : '—'}</small>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ── Funnel ──────────────────────────────────────────────────────────────────

export function Funnel({ stages }: { stages: Array<{ label: string; count: number; pct: number }> }): ReactNode {
  return (
    <ul className="funnel">
      {stages.map((stage) => (
        <li key={stage.label}>
          <div className="funnel__row">
            <span>{stage.label}</span>
            <strong>{stage.count.toLocaleString('en-IN')}</strong>
          </div>
          <div className="funnel__bar">
            <span style={{ width: `${Math.min(100, stage.pct)}%` }} />
          </div>
          <small>{stage.pct}%</small>
        </li>
      ))}
    </ul>
  )
}

/** The palette charts draw from when the API does not name one per slice. */
export const CHART_COLORS = [
  'var(--brand)',
  'var(--ok)',
  'var(--warn)',
  'var(--accent)',
  'var(--info)',
  'var(--bad)',
  '#0891b2',
  '#db2777',
]
